<template>
  <div class="alarm-cfg">
    <div class="content">
      <div class="page-header">
        <div>
          <h2>告警规则配置</h2>
          <div class="desc">管理告警推送间隔、轮询频率、接收人及电站告警开关</div>
        </div>
        <div class="header-actions">
          <v-btn variant="outlined" rounded="lg" class="text-none" :disabled="!canOperateActiveTab" @click="resetAll">恢复默认</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            min-width="120"
            class="text-none"
            prepend-icon="mdi-content-save-outline"
            :disabled="!canOperateActiveTab"
            @click="saveAllConfig"
          >
            保存全部配置
          </v-btn>
        </div>
      </div>

      <div class="seg-tabs">
        <div
          v-for="item in visibleTabs"
          :key="item.value"
          class="seg-tab"
          :class="{ active: activeTab === item.value }"
          @click="activeTab = item.value"
        >
          <span :class="item.icon"></span> {{ item.label }}
        </div>
      </div>

      <div v-if="hasTab('push')" class="tab-panel" :class="{ active: activeTab === 'push' }">
        <PushIntervalPanel />
      </div>

      <div v-if="hasTab('poll')" class="tab-panel" :class="{ active: activeTab === 'poll' }">
        <PollIntervalPanel />
      </div>

      <div v-if="hasTab('receiver')" class="tab-panel" :class="{ active: activeTab === 'receiver' }">
        <ReceiverPanel />
      </div>

      <div v-if="hasTab('station')" class="tab-panel" :class="{ active: activeTab === 'station' }">
        <StationAlarmPanel />
      </div>

      <div v-if="hasTab('station-alarm-code')" class="tab-panel" :class="{ active: activeTab === 'station-alarm-code' }">
        <StationAlarmCodePanel />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useAlarmConfig } from './useAlarmConfig'
import useUserStore from '@/store/modules/user'
import PushIntervalPanel from './panels/PushIntervalPanel.vue'
import PollIntervalPanel from './panels/PollIntervalPanel.vue'

// Panels
import ReceiverPanel from './panels/ReceiverPanel.vue'
import StationAlarmPanel from './panels/StationAlarmPanel.vue'
import StationAlarmCodePanel from './panels/StationAlarmCodePanel.vue'

const { activeTab, saveAllConfig, resetAll } = useAlarmConfig()
const userStore = useUserStore()
const { permissions } = storeToRefs(userStore)
const tabs = [
  { value: 'push', label: '推送间隔', icon: 'mdi mdi-bell-ring-outline', perms: ['ops:alarmConfig:pushInterval'] },
  { value: 'poll', label: '轮询间隔', icon: 'mdi mdi-timer-outline', perms: ['ops:alarmConfig:pollInterval'] },
  { value: 'receiver', label: '推送接收人', icon: 'mdi mdi-account-multiple-outline', perms: ['ops:alarmConfig:receiver'] },
  { value: 'station', label: '电站告警开关(运营)', icon: 'mdi mdi-office-building-cog-outline', perms: ['ops:alarmConfig:stationSwitch'] },
  { value: 'station-alarm-code', label: '电站告警开关(设备)', icon: 'mdi mdi-office-building-cog-outline', perms: ['ops:alarmConfig:stationSwitch'] },
]

function hasPerms(perms) {
  if (!Array.isArray(perms) || !perms.length) return true
  return (permissions.value || []).some(permission => permission === '*:*:*' || perms.includes(permission))
}

const visibleTabs = computed(() => tabs.filter(item => hasPerms(item.perms)))
const canOperateActiveTab = computed(() => {
  const current = tabs.find(item => item.value === activeTab.value)
  return current ? hasPerms(current.perms) : false
})

function hasTab(value) {
  return visibleTabs.value.some(item => item.value === value)
}

watch(visibleTabs, (list) => {
  if (!list.length) return
  if (!list.some(item => item.value === activeTab.value)) {
    activeTab.value = list[0].value
  }
}, { immediate: true, flush: 'post' })
</script>

<style scoped lang="scss">
/* 与根页面 variables.scss / gm-card 体系对齐：主色 #2B57EE、正文 #444a6d、表格式边框 */
.alarm-cfg {
  --primary: var(--color-primary);
  --primary-light: color-mix(in srgb, var(--color-primary) 14%, #ffffff);
  --primary-faint: color-mix(in srgb, var(--color-primary) 8%, #f4f6fd);
  --secondary: #48a9a6;
  --success: var(--color-success);
  --warning: var(--color-warning);
  --danger: var(--color-error);
  --bg: transparent;
  --card-bg: rgba(255, 255, 255, 0.82);
  --text-primary: var(--color-text);
  --text-secondary: color-mix(in srgb, var(--color-text) 78%, #7a8199);
  --text-muted: color-mix(in srgb, var(--color-text) 55%, #a0a6bd);
  --border: #e9efff;
  --surface-muted: #eef1fb;
  --shadow-card: 0 2px 8px rgba(8, 40, 87, 0.05);
  --cat-power: var(--color-warning);
  --cat-comm: var(--color-primary);
  --cat-volt: #e53935;
  --cat-temp: #7c3aed;
  min-height: 0;
  background: var(--bg);
  color: var(--text-primary);
  border-radius: 0;
}

.content {
  padding: 16px 20px 20px;
  max-width: 100%;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 12px 20px;
  margin-left: -20px;
  margin-right: -20px;
  margin-top: -16px;
  padding-top: 16px;
  background: color-mix(in srgb, var(--card-bg) 92%, transparent);
  backdrop-filter: blur(6px);
  border-bottom: 1px solid var(--color-td-border);
}

.page-header .header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.page-header .desc {
  font-size: 13px;
  color: var(--text-muted);
  margin-top: 4px;
}

.seg-tabs {
  display: inline-flex;
  background: var(--surface-muted);
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
  margin-bottom: 20px;
  border: 1px solid color-mix(in srgb, var(--color-primary) 8%, var(--border));
}

.seg-tab {
  padding: 8px 20px;
  font-size: 13px;
  color: var(--text-secondary);
  border-radius: 8px;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 6px;
}

.seg-tab:hover {
  color: var(--text-primary);
}

.seg-tab.active {
  background: #fff;
  color: var(--primary);
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(43, 87, 238, 0.1);
}

.seg-tab .mdi {
  font-size: 16px;
}

.tab-panel {
  display: none;
}

.tab-panel.active {
  display: block;
}

@media (prefers-reduced-motion: reduce) {
  .seg-tab {
    transition: none;
  }
}
</style>
